// shared reactive state for the horizon nav chrome (FI-1869). the top bar
// and the rail are separate components; this keeps collapse + mobile-drawer
// state in one observable so neither owns the other.
import Vue from 'vue'

const COLLAPSE_KEY = 'fi_nav_collapsed'

function initialCollapsed() {
  // an explicit user choice always wins and is never auto-clobbered by
  // navigation (the legacy shells' route-watcher collapse was a defect).
  // with no saved choice, small viewports start collapsed.
  try {
    const saved = localStorage.getItem(COLLAPSE_KEY)
    if (saved === '1') return true
    if (saved === '0') return false
  } catch (_) { /* storage unavailable */ }
  return typeof window !== 'undefined' && window.innerWidth < 1240
}

// v2: the map went tri-state (true = user-opened, false = user-collapsed,
// absent = follow the active-section rule), so the old key's accumulated
// auto-expansions must not carry over as explicit opens
const EXPAND_KEY = 'fi_nav_expanded2'

function initialExpanded() {
  try {
    const saved = JSON.parse(localStorage.getItem(EXPAND_KEY) || '{}')
    if (saved && typeof saved === 'object') return saved
  } catch (_) { /* storage unavailable or corrupt */ }
  return {}
}

export const navState = Vue.observable({
  collapsed: initialCollapsed(),
  mobileOpen: false,
  // per-section tree expansion overrides. keys are present only where the
  // user toggled: true sticks a section open across navigation, false holds
  // it closed; a section with NO key renders open exactly while it is the
  // active section (the rail decides that -- see fi_section_nav.isExpanded)
  expanded: initialExpanded(),
})

// touch-primary devices (phones/tablets report hover:none regardless of
// viewport size) emulate a mouseenter on tap, which pops the chrome's hover
// tooltips and leaves them stuck open over the popovers -- tooltip builders
// return an empty config when this is true. evaluated per call so tests can
// stub matchMedia.
export function noHoverDevice() {
  try {
    return !!(window.matchMedia && window.matchMedia('(hover: none)').matches)
  } catch (_) {
    return false
  }
}

export function setCollapsed(value) {
  navState.collapsed = !!value
  try { localStorage.setItem(COLLAPSE_KEY, value ? '1' : '0') } catch (_) { /* ignore */ }
}

export function toggleCollapsed() {
  setCollapsed(!navState.collapsed)
}

export function setMobileOpen(value) {
  navState.mobileOpen = !!value
}

export function setSectionExpanded(key, value) {
  // replace the object so vue 2 reactivity picks up new keys
  navState.expanded = { ...navState.expanded, [key]: !!value }
  try { localStorage.setItem(EXPAND_KEY, JSON.stringify(navState.expanded)) } catch (_) { /* ignore */ }
}

// drop a section's override so it falls back to the active-section rule
// (used when the user arrives in a section they had explicitly collapsed)
export function clearSectionExpanded(key) {
  const next = { ...navState.expanded }
  delete next[key]
  navState.expanded = next
  try { localStorage.setItem(EXPAND_KEY, JSON.stringify(navState.expanded)) } catch (_) { /* ignore */ }
}
